export const MODES = Object.freeze({
  DELIVERY: 'delivery',
  CLICK_COLLECT: 'click_collect',
  MIXED: 'mixed_mode',
});

export const CART_INFO_KEY = '_cart_info';

export const CART_INFO_VERSION = 1;

export const TIMING = Object.freeze({
  searchDebounce: 120,
  panelTransition: 350,
  notificationHide: 5000,
  selectorOpenDelay: 100,
  geoTimeout: 10000,
  geoMaxAge: 300000,
});

export const SEL = Object.freeze({
  storeRadio: '[data-store-radio]',
  storeItem: '[data-store-item]',
  storeList: '[data-store-list]',
  storeDistance: '[data-store-distance]',
  storeEmpty: '[data-store-empty]',
  storeEmptyText: '[data-store-empty-text]',
  storeResultsLive: '[data-store-results-live]',
  storeFooter: '[data-store-footer]',
  stockFilterToggle: '[data-store-stock-filter]',
  searchInput: '[data-store-search-input]',
  searchTriggerLocation: '[data-store-use-location]',
  locationMessage: '[data-store-location-message]',
  confirmStore: '[data-action="confirm-store"]',
  cancelStore: '[data-action="cancel-store"]',
  pdpDrawer: '[data-store-selector-drawer]',
  pdpPanels: '[data-store-pdp-panels]',
  pdpProductSummary: '[data-store-pdp-product-summary]',
  pdpDrawerContent: '[data-store-pdp-drawer-content]',
  clickCollectDefault: '[data-role="click-collect-default"]',
  openDrawerFiltered: '[data-action="open-drawer-filtered"]',
  openDrawer: '[data-action="open-drawer"]',
  changeStoreButton: '[data-action="change-store"]',
  cartStorePanel: '[data-store-selector-panel]',
  cartConciliationPanel: '[data-store-conciliation-panel]',
  cartCurrentStoreRoot: '[data-role="store-selector-current"]',
  cartSelectedStoreSummary: '[data-role="selected-store-summary"]',
  cartNoStore: '[data-role="no-store"]',
  checkoutWarning: '[data-role="checkout-warning"]',
  checkoutBtn: '[data-checkout-btn]',
  confirmChangesBtn: '[data-action="confirm-changes"]',
});

export const EVENTS = Object.freeze({
  STORE_CHANGED: 'on:store:changed',
  SECTIONS_UPDATED: 'dispatch:cart-click-collect:sections-updated',
  OPEN_SELECTOR: 'dispatch:cart-click-collect:open-store-selector',
});
const DEBUG_STORAGE_KEY = 'click-collect-debug';
const isDebugEnabled = () => {
  try {
    return localStorage.getItem(DEBUG_STORAGE_KEY) === '1' || new URLSearchParams(window.location.search).has('clickcollectdebug');
  } catch {
    return false;
  }
};

export const debug = Object.freeze({
  log(step, payload) {
    if (!isDebugEnabled()) return;

    console.warn('[store-selector]', step, payload || {}); // eslint-disable-line no-console
  },
  error(step, error, payload) {
    if (!isDebugEnabled()) return;

    const data = { error: error?.message || error, ...(payload || {}) };
    console.error('[store-selector]', step, data); // eslint-disable-line no-console
  },
});

export const debounce = (fn, wait) => {
  let id;
  return function debouncedFn(...args) {
    clearTimeout(id);
    id = setTimeout(() => fn.apply(this, args), wait || 120);
  };
};

export const cssEscape = (value) => {
  const raw = value == null ? '' : String(value);
  if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(raw);

  return raw.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
};

const stringifyValue = (value) => (value == null ? '' : String(value));

export const safeAsync = async (label, asyncFn, opts) => {
  try {
    const value = await asyncFn();
    return { ok: true, value };
  } catch (error) {
    debug.error(`safeAsync:${label}`, error);
    opts?.onError?.(error);
    return { ok: false, error };
  }
};

export const normalizeMode = (value, fallback = MODES.DELIVERY) => {
  if (value === MODES.MIXED) return MODES.MIXED;

  if (value === MODES.CLICK_COLLECT) return MODES.CLICK_COLLECT;

  if (value === MODES.DELIVERY) return MODES.DELIVERY;

  if (fallback === MODES.MIXED) return MODES.MIXED;

  return fallback === MODES.CLICK_COLLECT ? MODES.CLICK_COLLECT : MODES.DELIVERY;
};

export const normalizeStore = (store) => {
  if (!store?.id) return null;

  return {
    id: stringifyValue(store.id),
    name: stringifyValue(store.name),
    address: stringifyValue(store.address),
    phone: stringifyValue(store.phone ?? store.store_phone ?? store.storePhone),
    salonPhone: stringifyValue(store.salonPhone ?? store.salon_phone ?? store.salonPhoneNumber),
  };
};

export const normalizeStoreList = (stores) => {
  const list = stores instanceof Map
    ? [...stores.values()]
    : Array.isArray(stores)
      ? stores
      : [];
  const normalized = [];
  const seen = new Set();
  for (const store of list) {
    const candidate = typeof store === 'string' || typeof store === 'number'
      ? normalizeStore({ id: store })
      : normalizeStore(store);
    const storeId = stringifyValue(candidate?.id).trim();
    if (!storeId || seen.has(storeId)) continue;

    seen.add(storeId);
    normalized.push(candidate);
  }

  return normalized;
};

const mergeStoreLists = (...lists) => normalizeStoreList(lists.flatMap((list) => {
  if (!list) return [];

  if (list instanceof Map) return [...list.values()];

  return Array.isArray(list) ? list : [list];
}));

const serializeStoreIds = (stores) => normalizeStoreList(stores)
  .map((store) => store.id)
  .join('|');

const getCartInfoMode = (mode) => {
  if (mode === MODES.MIXED) return MODES.MIXED;

  return mode === MODES.CLICK_COLLECT ? MODES.CLICK_COLLECT : MODES.DELIVERY;
};

const TRACE_ID_KEY = 'store-selector-trace-id';

/**
 * A cart-lifetime id that lets a Shopify Function log line be matched to the
 * order it belongs to.
 *
 * Function run logs carry no cart or customer identity, and `_cart_info` is the
 * only thing the functions already read that also survives into the order as an
 * order attribute. Riding along in that payload is what makes the join possible,
 * for guests too. Cleared in `clearSelection`, so the next cart gets a new id.
 */
const readOrCreateTraceId = () => {
  try {
    const existing = String(localStorage.getItem(TRACE_ID_KEY) || '').trim();
    if (existing) return existing;

    const id = (crypto?.randomUUID?.() || `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`)
      .replace(/-/g, '')
      .slice(0, 12);
    localStorage.setItem(TRACE_ID_KEY, id);

    return id;
  } catch {
    return '';
  }
};

export const buildCartInfoPayload = (fulfillmentMode, store, options = {}) => {
  const mode = getCartInfoMode(fulfillmentMode);
  const selectedStore = mode === MODES.CLICK_COLLECT ? normalizeStore(store) : null;
  const stores = mergeStoreLists(options.stores, selectedStore);
  return {
    version: CART_INFO_VERSION,
    mode,
    trace_id: readOrCreateTraceId(),
    /* When this selection was written, so a function's run input shows whether it
       reads the current cart or a copy checkout froze earlier. Every write stamps
       it, whichever path wrote it. Comparisons ignore it - see cartAttributesMatch
       in product-form.js - so a fresh stamp never triggers a needless rewrite. */
    selected_at: options.selectedAt || new Date().toISOString(),
    store_id: selectedStore?.id || '',
    store_name: selectedStore?.name || '',
    store_address: selectedStore?.address || '',
    store_phone: selectedStore?.phone || '',
    salon_phone: selectedStore?.salonPhone || '',
    store_ids: serializeStoreIds(stores),
  };
};

export const stringifyCartInfo = (fulfillmentMode, store, options) => (
  JSON.stringify(buildCartInfoPayload(fulfillmentMode, store, options))
);

export const buildLineCartInfoPayload = (fulfillmentMode, store) => {
  const mode = getCartInfoMode(fulfillmentMode);
  const selectedStore = mode === MODES.CLICK_COLLECT ? normalizeStore(store) : null;
  return {
    version: CART_INFO_VERSION,
    mode,
    store_name: selectedStore?.name || '',
  };
};

export const stringifyLineCartInfo = (fulfillmentMode, store) => JSON.stringify(buildLineCartInfoPayload(fulfillmentMode, store));

export const parseCartInfo = (value) => {
  if (!value) return null;

  let parsed = value;
  if (typeof value === 'string') {
    const raw = value.trim();
    if (!raw) return null;

    try {
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

  const mode = parsed.mode === MODES.CLICK_COLLECT
    ? MODES.CLICK_COLLECT
    : parsed.mode === MODES.DELIVERY
      ? MODES.DELIVERY
      : parsed.mode === MODES.MIXED
        ? MODES.MIXED
        : null;
  if (!mode) return null;

  const store = normalizeStore({
    id: parsed.store_id ?? parsed.storeId ?? parsed.id,
    name: parsed.store_name ?? parsed.storeName ?? parsed.name,
    address: parsed.store_address ?? parsed.storeAddress ?? parsed.address,
    phone: parsed.store_phone ?? parsed.storePhone ?? parsed.phone,
    salonPhone: parsed.salon_phone ?? parsed.salonPhone,
  });
  const rawStoreIds = parsed.store_ids ?? parsed.storeIds;
  const storeIds = Array.isArray(rawStoreIds)
    ? rawStoreIds
    : stringifyValue(rawStoreIds).split('|');
  const stores = mergeStoreLists(
    Array.isArray(parsed.stores) ? parsed.stores : [],
    storeIds,
    store,
  );

  return {
    version: Number(parsed.version) || CART_INFO_VERSION,
    mode,
    store: mode === MODES.CLICK_COLLECT ? store : null,
    stores,
    raw: parsed,
  };
};

export const readStoreFromItem = (item) => {
  if (!item) return null;

  const id = String(item.dataset.storeId || '').trim();
  if (!id) return null;

  return {
    id,
    name: item.dataset.storeName || '',
    address: item.dataset.storeAddress || '',
    phone: item.dataset.storePhone || '',
    salonPhone: item.dataset.salonPhone || '',
  };
};

const FULFILLMENT_STORAGE_KEY = 'store-selector-fulfillment-mode';
const LAST_CNC_STORE_KEY = 'store-selector-last-cnc-store';
const CART_BINDING_KEY = 'store-selector-cart-binding';

const readCartBinding = () => {
  try {
    return String(localStorage.getItem(CART_BINDING_KEY) || '').trim();
  } catch {
    return '';
  }
};

/**
 * Records the store that was last written into the cart's `_cart_info`.
 *
 * This is what separates "the buyer committed this store to a cart" from "the
 * buyer only has a store selected while browsing". Without that distinction a
 * completed order and a PDP-only preference look identical, because both leave a
 * cart with no `_cart_info`. Every writer of the cart attribute must call this.
 */
export const noteCartBinding = (store) => {
  const storeId = normalizeStore(store)?.id || '';
  try {
    if (storeId) localStorage.setItem(CART_BINDING_KEY, storeId);
    else localStorage.removeItem(CART_BINDING_KEY);
  } catch {
    /* no-op */
  }
};

/**
 * A committed store must not outlive the cart it was committed to.
 *
 * Shopify clears cart attributes when an order completes, so a binding that no
 * longer matches the cart means that cart is gone - the order was placed, or the
 * cart expired. The remembered store has to go with it. If it survives, the next
 * cart silently inherits the previous order's store, and because Shopify locks
 * the pickup location from `_cart_info` the order can be fulfilled at the
 * previous order's location even after the buyer picks a different one.
 */
const dropSelectionIfCartGone = (serverStoreId) => {
  const binding = readCartBinding();
  if (!binding || binding === serverStoreId) return;

  clearSelection();
};

const readLastCncStore = () => {
  try {
    const raw = localStorage.getItem(LAST_CNC_STORE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const id = String(parsed?.id || '').trim();
    if (!id) return null;

    return normalizeStore(parsed);
  } catch {
    return null;
  }
};

export const readSelection = () => {
  let mode = MODES.DELIVERY;
  let store = null;
  try {
    const storedMode = localStorage.getItem(FULFILLMENT_STORAGE_KEY);
    if (storedMode === MODES.CLICK_COLLECT) mode = MODES.CLICK_COLLECT;
    else if (storedMode === MODES.MIXED) mode = MODES.MIXED;
  } catch {
    /* no-op */
  }

  try {
    const parsed = window.theme?.storageUtil?.get('selected-store', true);
    if (parsed && typeof parsed === 'object') {
      const id = String(parsed.id || '').trim();
      if (id) store = normalizeStore(parsed);
    }
  } catch {
    /* no-op */
  }

  return { mode, store };
};

export const writeSelection = (mode, store) => {
  try {
    const value = mode === MODES.CLICK_COLLECT || mode === MODES.MIXED ? mode : MODES.DELIVERY;
    localStorage.setItem(FULFILLMENT_STORAGE_KEY, value);
  } catch {
    /* no-op */
  }

  const normalized = normalizeStore(store);
  try {
    if (normalized) window.theme?.storageUtil?.set('selected-store', normalized);
    else window.theme?.storageUtil?.remove('selected-store');
  } catch {
    /* no-op */
  }

  if (normalized && (mode === MODES.CLICK_COLLECT || mode === MODES.MIXED)) {
    try {
      localStorage.setItem(LAST_CNC_STORE_KEY, JSON.stringify(normalized));
    } catch {
      /* no-op */
    }
  }
};

export const clearSelection = () => {
  try {
    localStorage.removeItem(FULFILLMENT_STORAGE_KEY);
  } catch {
    /* no-op */
  }

  try {
    window.theme?.storageUtil?.remove('selected-store');
  } catch {
    /* no-op */
  }

  try {
    localStorage.removeItem(LAST_CNC_STORE_KEY);
  } catch {
    /* no-op */
  }

  try {
    localStorage.removeItem(CART_BINDING_KEY);
  } catch {
    /* no-op */
  }

  try {
    localStorage.removeItem(TRACE_ID_KEY);
  } catch {
    /* no-op */
  }
};

export const resolveInitialState = (serverAttrs) => {
  const serverMode = normalizeMode(serverAttrs?.mode);
  const serverStore = normalizeStore(serverAttrs?.store);
  dropSelectionIfCartGone(serverStore?.id || '');
  const { mode: storedMode, store: storedStore } = readSelection();

  /* An empty cart carries no commitment: Shopify keeps no attributes for it, so
     the server can only ever render delivery with no store. Trusting that would
     put the Delivery tab in front of a buyer who has a store selected for
     browsing, so the stored selection stands until the cart has lines of its own. */
  if (serverAttrs?.committed === false) return { mode: storedMode, store: storedStore };

  if (serverMode === MODES.CLICK_COLLECT && serverStore) {
    writeSelection(serverMode, serverStore);
    return { mode: serverMode, store: serverStore };
  }

  if (serverMode === MODES.MIXED) {
    return { mode: MODES.MIXED, store: serverStore || storedStore };
  }

  if (serverMode === MODES.CLICK_COLLECT) {
    return { mode: MODES.CLICK_COLLECT, store: serverStore || storedStore };
  }

  return { mode: MODES.DELIVERY, store: serverStore || storedStore };
};

const resolveRememberedStore = (candidates, resolver) => {
  for (const candidate of candidates.map(normalizeStore).filter(Boolean)) {
    const resolved = resolver ? resolver(candidate.id, candidate) : candidate;
    const normalized = normalizeStore(resolved);
    if (normalized) return normalized;
  }

  return null;
};

export class SelectionState {
  constructor() {
    this.mode = MODES.DELIVERY;
    this.store = null;
    this.pendingStore = null;
  }

  restore(serverAttrs) {
    const resolved = resolveInitialState(serverAttrs);
    this.mode = normalizeMode(resolved.mode);
    this.store = normalizeStore(resolved.store);
    this.pendingStore = null;
  }

  persist() {
    writeSelection(this.mode, this.store);
  }

  persistCommittedSelection({ storeResolver = null } = {}) {
    writeSelection(this.mode, this.store || this.getRememberedStore(storeResolver));
  }

  getMode() {
    return normalizeMode(this.mode);
  }

  getSelectedStore() {
    return this.getMode() === MODES.CLICK_COLLECT ? normalizeStore(this.store) : null;
  }

  getRememberedStore(resolver) {
    return resolveRememberedStore([this.store, this.pendingStore, readSelection().store, readLastCncStore()], resolver);
  }
}

export class ServerSelectionState extends SelectionState {
  constructor() {
    super();
    this.serverMode = MODES.DELIVERY;
    this.serverStore = null;
  }

  restore(serverAttrs) {
    super.restore(serverAttrs);
    this.serverMode = normalizeMode(serverAttrs?.mode);
    this.serverStore = normalizeStore(serverAttrs?.store);
  }

  syncFromServer() {
    this.mode = normalizeMode(this.serverMode);
    this.store = normalizeStore(this.serverStore);
    this.pendingStore = null;
  }

  setServerSelection(mode, store) {
    this.serverMode = normalizeMode(mode);
    this.serverStore = normalizeStore(store);
  }
}

export const createSyncQueue = () => {
  let queue = Promise.resolve(true);
  return {
    enqueue(fn) {
      const queued = queue.then(fn, fn);
      queue = queued.catch(() => true);
      return queued;
    },
  };
};

const STOREFRONT_API_VERSION = '2026-07';

const DELIVERY_PREFERENCE_MUTATION = `mutation SetDeliveryPreference($cartId: ID!, $delivery: CartDeliveryPreferenceInput!) {
  cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: { preferences: { delivery: $delivery } }) {
    userErrors { field message }
  }
}`;

const deliveryPreference = (mode, store) => {
  const selected = mode === MODES.CLICK_COLLECT ? normalizeStore(store) : null;

  return selected?.id
    ? { deliveryMethod: ['PICK_UP'], pickupHandle: [String(selected.id)] }
    : { deliveryMethod: ['SHIPPING'] };
};

/**
 * Tells checkout which delivery method - and which store - to open on.
 *
 * `_cart_info` is invisible to checkout's delivery section. It reads buyer
 * identity preferences instead, and those can only be set through the Storefront
 * API, which is why this is the one place the theme leaves the Ajax cart API.
 * The cart is the same object: `/cart.js` returns a token that doubles as the
 * Storefront cart id, secret key included.
 *
 * Preferences prefill, they do not enforce - a buyer who changes store inside
 * checkout is stopped by the pickup-location-validation function, not here. With
 * no token configured this does nothing, and checkout falls back to whichever
 * method the buyer used last.
 */
const syncDeliveryPreference = async (cart, mode, store) => {
  const token = window.theme?.settings?.storefrontApiToken;
  const cartToken = stringifyValue(cart?.token).trim();
  if (!token || !cartToken) return false;

  try {
    const response = await fetch(`/api/${STOREFRONT_API_VERSION}/graphql.json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
      body: JSON.stringify({
        query: DELIVERY_PREFERENCE_MUTATION,
        variables: {
          cartId: `gid://shopify/Cart/${cartToken}`,
          delivery: deliveryPreference(mode, store),
        },
      }),
    });

    const body = await response.json();
    const failure = (body?.errors || body?.data?.cartBuyerIdentityUpdate?.userErrors || [])[0];
    if (failure) {
      debug.error('syncDeliveryPreference:rejected', failure.message, { mode });
      return false;
    }

    debug.log('syncDeliveryPreference:ok', { mode, store: store?.id || '' });
    return true;
  } catch (error) {
    debug.error('syncDeliveryPreference:exception', error, { mode });
    return false;
  }
};

export const updateCartAttributes = (syncQueue, mode, store, options = {}) => {
  const attributes = {
    [CART_INFO_KEY]: stringifyCartInfo(mode, store, {
      stores: options.stores,
      selectedAt: new Date().toISOString(),
    }),
  };
  const sections = Array.isArray(options.sections)
    ? [...new Set(options.sections.filter(Boolean))]
    : [];

  return syncQueue.enqueue(async () => {
    try {
      const body = { attributes };
      if (sections.length) {
        body.sections = sections;
        body.sections_url = options.sectionsUrl || window.location.pathname;
      }

      const response = await fetch(window.theme.routes.cartUpdate, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        let body = '';
        try {
          body = await response.text();
        } catch {
          /* no-op */
        }

        debug.error('updateCartAttributes:failed', 'non_ok_response', { status: response.status, body: body.slice(0, 400) });
        return { ok: false, cart: null };
      }

      let cart = null;
      try {
        cart = await response.json();
      } catch (err) {
        debug.error('updateCartAttributes:json_parse_failed', err, { mode, status: response.status });
      }

      /* Shopify keeps no cart attributes while the cart has no lines, so an empty
         cart cannot hold a commitment - the write above returns the attributes in
         its response and then drops them. Recording a binding for it would make
         the next cart render, which reports delivery and no store for an empty
         cart, look like the cart the store was committed to is gone, and
         `dropSelectionIfCartGone` would wipe the selection the buyer just made
         while browsing. */
      if (Array.isArray(cart?.items) && cart.items.length > 0) {
        noteCartBinding(mode === MODES.CLICK_COLLECT ? store : null);
      }

      /* Checkout regenerates its pickup options only when the cart itself changes,
         and an attribute write does not count as a change. Without this, Shopify
         keeps the option it generated for the previous store and offers a store the
         buyer no longer has selected - MAXIZOO-832, where the cart said Skejby and
         checkout offered only Aabenraa. Rewriting the line properties is a real
         mutation, so the pickup generator is asked again with the current
         `_cart_info`. */
      const syncLines = window.theme?.fulfillmentModeUtil?.syncCartLineFulfillment;
      if (typeof syncLines === 'function') {
        try {
          await syncLines({ fulfillmentMode: mode, store, cart });

          // Line keys change with their properties, so hand callers a current cart.
          const refreshed = await fetch(`${window.theme.routes.cart}.js`, { headers: { Accept: 'application/json' } });
          if (refreshed.ok) cart = await refreshed.json();
        } catch (error) {
          debug.error('updateCartAttributes:lineSyncFailed', error, { mode });
        }
      }

      /* Awaited on purpose: the buyer can hit checkout the moment this resolves,
         and a preference that lands after the checkout has rendered is useless. */
      await syncDeliveryPreference(cart, mode, store);

      return { ok: true, cart };
    } catch (error) {
      debug.error('updateCartAttributes:exception', error, { mode });
      return { ok: false, cart: null };
    }
  });
};

export const refreshCartDrawer = async () => {
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer?.refreshCartDrawer) await cartDrawer.refreshCartDrawer(true);
};

export const getStockStatus = (qty, thresholdLow, thresholdVeryLow) => {
  const low = thresholdLow ?? 10;
  const veryLow = thresholdVeryLow ?? 2;
  const strings = window.theme.strings.storeSelector;
  if (qty <= 0) return { label: strings.outOfStock, cssClass: 'out-of-stock' };

  if (qty <= veryLow) return { label: strings.veryLowStock, cssClass: 'very-low-stock' };

  if (qty <= low) return { label: strings.lowStock, cssClass: 'low-stock' };

  return { label: strings.inStock, cssClass: 'in-stock' };
};

const extractLines = (inventoryData) => {
  if (!inventoryData) return [];

  if (Array.isArray(inventoryData.lines)) return inventoryData.lines;

  const variants = inventoryData.variants;
  if (!variants || typeof variants !== 'object') return [];

  return Object.entries(variants).map(([variantId, data]) => ({ variantId, ...data }));
};

export const createEmptyCartStockSummary = () => ({
  allInStock: true,
  unavailableItems: [],
  partialItems: [],
  totalItems: 0,
  availableCount: 0,
  totalRequestedQuantity: 0,
  unavailableQuantity: 0,
  hasLowStock: false,
  hasVeryLowStock: false,
});
const updateCartStockFlags = (result, availableQty, thresholdLow, thresholdVeryLow) => {
  if (availableQty <= 0) return;

  if (availableQty <= thresholdVeryLow) {
    result.hasLowStock = true;
    result.hasVeryLowStock = true;
    return;
  }

  if (availableQty <= thresholdLow) result.hasLowStock = true;
};

const buildCartStockSummary = (inventoryData, { isSupported, getAvailableQuantity, fulfilledQtyKey }) => {
  const lines = extractLines(inventoryData);
  const result = createEmptyCartStockSummary();
  if (!lines.length) return result;

  const thresholdLow = Number(inventoryData?.thresholdLow ?? 10);
  const thresholdVeryLow = Number(inventoryData?.thresholdVeryLow ?? 2);
  const remainingByVariant = new Map();
  const availableQtyByVariant = new Map();
  for (const data of lines) {
    if (!data || typeof data !== 'object') continue;

    result.totalItems += 1;
    const requestedQty = Number(data.quantity) || 0;
    result.totalRequestedQuantity += requestedQty;
    const variantKey = String(data.variantId ?? data.id ?? '');
    if (!availableQtyByVariant.has(variantKey)) {
      const availableQty = isSupported(data) ? Math.max(0, Number(getAvailableQuantity(data)) || 0) : 0;
      availableQtyByVariant.set(variantKey, availableQty);
      remainingByVariant.set(variantKey, availableQty);
      updateCartStockFlags(result, availableQty, thresholdLow, thresholdVeryLow);
    }

    const remainingQty = Math.max(0, Number(remainingByVariant.get(variantKey) ?? 0));
    const fulfilledQty = Math.min(requestedQty, remainingQty);
    const unavailableQty = Math.max(0, requestedQty - fulfilledQty);
    remainingByVariant.set(variantKey, Math.max(0, remainingQty - fulfilledQty));
    if (unavailableQty > 0) {
      result.allInStock = false;
      result.unavailableQuantity += unavailableQty;
      if (fulfilledQty <= 0) result.unavailableItems.push({ ...data, [fulfilledQtyKey]: 0 });
      else {
        result.partialItems.push({ ...data, [fulfilledQtyKey]: fulfilledQty });
        result.availableCount += 1;
      }

      continue;
    }

    result.availableCount += 1;
  }

  return result;
};

export const getStoreCartStock = (inventoryData, storeId) => {
  const storeIdStr = String(storeId);
  return buildCartStockSummary(inventoryData, {
    isSupported: (data) => data.supportsCnC !== false,
    getAvailableQuantity: (data) => data.inventories?.[storeIdStr] ?? data.inventories?.[Number(storeIdStr)] ?? 0,
    fulfilledQtyKey: 'storeQty',
  });
};

export const getDeliveryCartStock = (inventoryData) => {
  return buildCartStockSummary(inventoryData, {
    isSupported: (data) => data.supportsDelivery !== false,
    getAvailableQuantity: (data) => data.deliveryInventoryQuantity ?? data.delivery_inventory_quantity ?? 0,
    fulfilledQtyKey: 'deliveryQty',
  });
};

const inventoryCache = new WeakMap();
export const parseInventory = (scriptEl) => {
  if (!scriptEl) return null;

  if (inventoryCache.has(scriptEl)) return inventoryCache.get(scriptEl);

  try {
    const data = JSON.parse(scriptEl.textContent || '{}');
    inventoryCache.set(scriptEl, data);
    return data;
  } catch {
    inventoryCache.set(scriptEl, null);
    return null;
  }
};

export const $ = (selector, root = document) => root.querySelector(selector);

export const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
const STOCK_CLASSES = [
  'store-selector__stock--in-stock',
  'store-selector__stock--low-stock',
  'store-selector__stock--very-low-stock',
  'store-selector__stock--out-of-stock',
];
export const applyStockState = (stockEl, labelEl, status) => {
  if (!stockEl || !status) return;

  stockEl.classList.remove(...STOCK_CLASSES);
  stockEl.classList.add(`store-selector__stock--${status.cssClass}`);
  stockEl.hidden = false;
  if (labelEl) labelEl.textContent = status.label;
};

export const highlightStore = (container, storeId) => {
  if (!container) return;

  const targetId = storeId == null ? null : String(storeId);
  container.querySelectorAll(SEL.storeRadio).forEach((radio) => {
    const isSelected = targetId !== null && radio.value === targetId;
    radio.checked = isSelected;
    radio.closest(SEL.storeItem)?.classList.toggle('store-selector__card--selected', isSelected);
  });
};

export const setStoreView = (root, selector, storeId) => {
  if (!root) return false;

  const targetId = storeId == null ? null : String(storeId);
  let visible = false;
  root.querySelectorAll(selector).forEach((view) => {
    const matches = targetId !== null && String(view.dataset.storeId || '') === targetId;
    view.hidden = !matches;
    if (matches) visible = true;
  });
  return visible;
};

export const showPanel = (panel, { animate = true } = {}) => {
  if (!panel) return;

  panel.hidden = false;
  panel.setAttribute('aria-hidden', 'false');
  if (!animate) {
    /* Snap straight to the open position. Used when the caller is about to
       animate a parent container, so the two don't slide in one after another. */
    panel.style.transition = 'none';
    panel.classList.add('is-active');
    void panel.offsetHeight; // flush the un-animated position before restoring
    panel.style.transition = '';
    return;
  }

  void panel.offsetHeight; // force reflow for transition
  panel.classList.add('is-active');
};

export const hidePanel = (panel) => {
  if (!panel) return;

  let done = false;
  const finalize = () => {
    if (done) return;

    done = true;
    panel.hidden = true;
    panel.removeEventListener('transitionend', finalize);
  };

  panel.classList.remove('is-active');
  panel.setAttribute('aria-hidden', 'true');
  panel.addEventListener('transitionend', finalize);
  setTimeout(finalize, TIMING.panelTransition);
};

export const activateTab = (host, mode) => {
  const tabMode = mode === MODES.CLICK_COLLECT ? MODES.CLICK_COLLECT : MODES.DELIVERY;
  const tab = host.querySelector(`[role="tab"][data-fulfillment="${tabMode}"]`);
  const tabbedContent = host.querySelector('tabbed-content');
  if (tab && tabbedContent?.activateTab) tabbedContent.activateTab(tab);
};

export const normalizeQuery = (value) =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'o')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/aa/g, 'a')
    .trim();

export const haversine = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const createStoreList = ({ getPanel, isInStock }) => {
  let searchQuery = '';
  let rawSearchQuery = '';
  let stockFilterEnabled = false;
  let userCoords = null;
  let storeDistances = null;
  let lastSearchAnalyticsKey = '';
  const getStoreItems = (root) => $$(SEL.storeItem, root || getPanel());
  const updateEmptyState = (visibleCount) => {
    const panel = getPanel();
    const emptyState = $(SEL.storeEmpty, panel);
    const emptyText = $(SEL.storeEmptyText, panel);
    if (!emptyState || !emptyText) return;

    if (visibleCount > 0) {
      emptyState.hidden = true;
      return;
    }

    emptyText.textContent = searchQuery ? window.theme.strings.storeSelector.emptySearchResults : window.theme.strings.storeSelector.emptyList;
    emptyState.hidden = false;
  };

  const applyFilters = () => {
    const panel = getPanel();
    if (!panel) return 0;

    let visibleCount = 0;
    for (const item of getStoreItems(panel)) {
      const normalizedSearchText =
        item.dataset.searchTextNormalized || (item.dataset.searchTextNormalized = normalizeQuery(item.dataset.searchText || ''));
      const matchesSearch = !searchQuery || normalizedSearchText.includes(searchQuery);
      const matchesStockFilter = !stockFilterEnabled || isInStock(item);
      const shouldShow = matchesSearch && matchesStockFilter;
      item.hidden = !shouldShow;
      if (shouldShow) visibleCount += 1;
    }

    updateEmptyState(visibleCount);
    const liveRegion = $(SEL.storeResultsLive, panel);
    if (liveRegion) {
      liveRegion.textContent = window.theme.strings.storeSelector.searchResultsCount.replace('[count]', String(visibleCount));
    }

    return visibleCount;
  };

  const applySearch = (rawQuery) => {
    rawSearchQuery = String(rawQuery || '');
    searchQuery = normalizeQuery(rawSearchQuery);
    const visibleCount = applyFilters();
    const analyticsKey = `${searchQuery}|${visibleCount}`;
    if (analyticsKey === lastSearchAnalyticsKey) return;

    lastSearchAnalyticsKey = analyticsKey;
    analytics('search', {
      query_length: searchQuery.length,
      results_count: visibleCount,
    });
  };

  const setStockFilter = (enabled) => {
    stockFilterEnabled = Boolean(enabled);
    const toggle = $(SEL.stockFilterToggle, getPanel());
    if (toggle) toggle.checked = stockFilterEnabled;

    applyFilters();
  };

  const syncStockFilterToggle = () => {
    const toggle = $(SEL.stockFilterToggle, getPanel());
    if (toggle) toggle.checked = stockFilterEnabled;
  };

  const calculateDistances = (lat, lng) => {
    const panel = getPanel();
    if (!panel) return;

    userCoords = { lat, lng };
    storeDistances = new Map();
    for (const item of getStoreItems(panel)) {
      const itemLat = Number(item.dataset.lat);
      const itemLng = Number(item.dataset.lng);
      if (!Number.isFinite(itemLat) || !Number.isFinite(itemLng)) continue;

      storeDistances.set(item.dataset.storeId, haversine(lat, lng, itemLat, itemLng));
    }
  };

  const sortByDistance = () => {
    const panel = getPanel();
    const list = $(SEL.storeList, panel);
    if (!panel || !list || !storeDistances?.size) return;

    const items = getStoreItems(list);
    items.sort((left, right) => {
      const leftDist = storeDistances.get(left.dataset.storeId) ?? Infinity;
      const rightDist = storeDistances.get(right.dataset.storeId) ?? Infinity;
      if (leftDist !== rightDist) return leftDist - rightDist;

      return (left.dataset.sortName || left.dataset.storeName || '').localeCompare(right.dataset.sortName || right.dataset.storeName || '', 'da', {
        sensitivity: 'base',
      });
    });
    for (const item of items) list.appendChild(item);
  };

  const renderDistances = () => {
    const panel = getPanel();
    if (!panel || !storeDistances) return;

    for (const item of getStoreItems(panel)) {
      const badge = $(SEL.storeDistance, item);
      if (!badge) continue;

      const distance = storeDistances.get(item.dataset.storeId);
      if (distance == null) {
        badge.hidden = true;
        badge.textContent = '';
        continue;
      }

      const distanceLabel = window.theme?.strings?.storeSelector?.distanceLabel || 'Afstand';
      badge.textContent = `${distanceLabel}: ${distance < 10 ? `${distance.toFixed(1)} km` : `${Math.round(distance)} km`}`;
      badge.hidden = false;
    }
  };

  const showLocationMessage = (text) => {
    const message = $(SEL.locationMessage, getPanel());
    if (!message) return;

    message.textContent = text;
    message.hidden = false;
  };

  const hideLocationMessage = () => {
    const message = $(SEL.locationMessage, getPanel());
    if (!message) return;

    message.textContent = '';
    message.hidden = true;
  };

  const markNearestInStockStore = () => {
    const panel = getPanel();
    if (!panel) return;

    for (const el of panel.querySelectorAll('[data-nearest-in-stock]')) {
      delete el.dataset.nearestInStock;
    }

    const nearest = getStoreItems(panel).find((item) => !item.hidden && isInStock(item));
    if (!nearest) return;

    nearest.dataset.nearestInStock = '';
  };

  const applyCoords = (coords) => {
    if (!coords || !Number.isFinite(coords.lat) || !Number.isFinite(coords.lng)) return false;

    hideLocationMessage();
    calculateDistances(coords.lat, coords.lng);
    sortByDistance();
    renderDistances();
    applyFilters();
    markNearestInStockStore();
    return true;
  };

  const setLocationBtnActive = (active) => {
    const panel = getPanel();
    if (!panel) return;

    const btn = panel.querySelector(SEL.searchTriggerLocation);
    if (btn) btn.classList.toggle('is-active', active);
  };

  const requestGeolocation = () => {
    if (!navigator.geolocation) {
      showLocationMessage(window.theme.strings.storeSelector.locationUnavailable);
      analytics('error', { code: 'geolocation_unsupported' });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        applyCoords({ lat: position.coords.latitude, lng: position.coords.longitude });
        setLocationBtnActive(true);
        analytics('use_location', { success: true });
      },
      (error) => {
        setLocationBtnActive(false);
        if (error.code === 1) {
          showLocationMessage(window.theme.strings.storeSelector.locationDenied);
          analytics('use_location', { success: false, code: 'permission_denied' });
          return;
        }

        showLocationMessage(window.theme.strings.storeSelector.locationUnavailable);
        analytics('error', { code: 'geolocation_unavailable' });
      },
      { enableHighAccuracy: false, timeout: TIMING.geoTimeout, maximumAge: TIMING.geoMaxAge },
    );
  };

  const requestGeolocationIfGranted = () => {
    if (userCoords) {
      applyCoords(userCoords);
      return;
    }

    if (!navigator.permissions) {
      applyFilters();
      return;
    }

    navigator.permissions
      .query({ name: 'geolocation' })
      .then((result) => {
        if (result.state === 'granted') requestGeolocation();
        else applyFilters();
      })
      .catch(() => applyFilters());
  };

  const highlight = (storeId) => highlightStore(getPanel(), storeId);
  const getStoreById = (storeId, fallback = {}) => {
    if (storeId == null || storeId === '') return null;

    const id = String(storeId);
    const escapedId = cssEscape(id);
    const item = getPanel()?.querySelector(`${SEL.storeItem}[data-store-id="${escapedId}"]`);
    if (!item && !fallback.id && !fallback.name && !fallback.address) return null;

    return {
      id,
      name: fallback.name || item?.dataset.storeName || '',
      address: fallback.address || item?.dataset.storeAddress || '',
      phone: fallback.phone || item?.dataset.storePhone || '',
      salonPhone: fallback.salonPhone || item?.dataset.salonPhone || '',
    };
  };

  /**
   * Initialize the list controller on a panel element.
   */
  const attach = (panel, { preserveState = false } = {}) => {
    if (!panel) return;

    const searchInputEl = $(SEL.searchInput, panel);
    const stockFilterToggleEl = $(SEL.stockFilterToggle, panel);
    if (preserveState) {
      if (searchInputEl) searchInputEl.value = rawSearchQuery;

      if (stockFilterToggleEl) stockFilterToggleEl.checked = stockFilterEnabled;
    } else {
      rawSearchQuery = searchInputEl?.value || '';
      searchQuery = normalizeQuery(rawSearchQuery);
      stockFilterEnabled = Boolean(stockFilterToggleEl?.checked);
    }

    if (userCoords) {
      applyCoords(userCoords);
      setLocationBtnActive(true);
    } else {
      applyFilters();
    }
  };

  return {
    attach,
    applySearch,
    applyFilters,
    setStockFilter,
    syncStockFilterToggle,
    requestGeolocation,
    requestGeolocationIfGranted,
    applyCoords,
    highlight,
    getStoreById,
    getStoreItems,
    get items() {
      return getStoreItems();
    },
    get searchQuery() {
      return rawSearchQuery;
    },
    set searchQuery(val) {
      rawSearchQuery = val;
    },
    get stockFilterEnabled() {
      return stockFilterEnabled;
    },
    set stockFilterEnabled(val) {
      stockFilterEnabled = Boolean(val);
    },
    get userCoords() {
      return userCoords;
    },
  };
};

export const dispatchStoreChanged = (store, previousStoreId, fulfillmentMode, extraDetail) => {
  const detail = Object.assign({ store, previousStoreId, fulfillmentMode }, extraDetail || {});
  document.dispatchEvent(new CustomEvent(EVENTS.STORE_CHANGED, { bubbles: true, detail }));
};

export const analytics = (action, payload) => {
  const eventPayload = Object.assign({ event: `click-collect_store_selector_${action}` }, payload || {});
  if (Array.isArray(window.dataLayer)) window.dataLayer.push(eventPayload);

  document.dispatchEvent(new CustomEvent('analytics:store-selector', { bubbles: true, detail: eventPayload }));
};

const fulfillmentModeUtil = (() => {
  const stringify = (value) => (value == null ? '' : String(value));

  const normalizeLineMode = (value, fallback = MODES.DELIVERY) => {
    if (value === MODES.CLICK_COLLECT) return MODES.CLICK_COLLECT;

    if (value === MODES.DELIVERY) return MODES.DELIVERY;

    if (fallback === MODES.CLICK_COLLECT) return MODES.CLICK_COLLECT;

    return MODES.DELIVERY;
  };

  const getCartInfoFromAttributes = (cart) => parseCartInfo(cart?.attributes?.[CART_INFO_KEY]);
  const getCartInfoFromLine = (item) => parseCartInfo(item?.properties?.[CART_INFO_KEY]);
  const getCartFallbackMode = (cart) => getCartInfoFromAttributes(cart)?.mode || MODES.DELIVERY;
  const getStoreNameFromCartInfo = (cartInfo) => stringify(
    cartInfo?.raw?.store_name ?? cartInfo?.raw?.storeName ?? cartInfo?.raw?.name ?? cartInfo?.store?.name,
  ).trim();
  const addStoreToMap = (stores, store) => {
    const normalized = normalizeStore(store);
    const storeId = stringify(normalized?.id).trim();
    if (!storeId || stores.has(storeId)) return;

    stores.set(storeId, normalized);
  };

  const sanitizeStoreForMode = (mode, store) => {
    if (mode !== MODES.CLICK_COLLECT || !store) return null;

    const normalized = normalizeStore(store);
    if (!normalized?.id) return null;

    return normalized;
  };

  const buildLineProperties = (existingProperties = {}, fulfillmentMode, store) => {
    const mode = normalizeLineMode(fulfillmentMode, MODES.DELIVERY);
    const selectedStore = sanitizeStoreForMode(mode, store);
    const properties = { ...(existingProperties || {}) };
    properties[CART_INFO_KEY] = stringifyLineCartInfo(mode, selectedStore);
    return properties;
  };

  const getLineFulfillmentMode = (item, fallbackMode = MODES.DELIVERY) => {
    const cartInfo = getCartInfoFromLine(item);
    if (cartInfo?.mode) return cartInfo.mode;

    return normalizeLineMode(fallbackMode, MODES.DELIVERY);
  };

  const getStoreFromLineItem = (item) => {
    const cartInfo = getCartInfoFromLine(item);
    if (cartInfo?.mode === MODES.CLICK_COLLECT && cartInfo.store?.id) return cartInfo.store;

    return null;
  };

  const getStoreNameFromLineItem = (item) => {
    return getStoreNameFromCartInfo(getCartInfoFromLine(item));
  };

  const getCartInfoStores = (cart) => {
    const stores = new Map();
    const cartInfo = getCartInfoFromAttributes(cart);
    for (const store of mergeStoreLists(cartInfo?.stores, cartInfo?.store)) {
      addStoreToMap(stores, store);
    }

    if (!Array.isArray(cart?.items)) return [...stores.values()];

    const fallbackMode = getCartFallbackMode(cart);
    for (const item of cart.items) {
      if (getLineFulfillmentMode(item, fallbackMode) !== MODES.CLICK_COLLECT) continue;

      addStoreToMap(stores, getStoreFromLineItem(item));
    }

    return [...stores.values()];
  };

  const getCartModes = (cart) => {
    const modes = new Set();
    if (!Array.isArray(cart?.items)) return modes;

    const cartInfoMode = getCartInfoFromAttributes(cart)?.mode;
    if (cartInfoMode === MODES.CLICK_COLLECT || cartInfoMode === MODES.DELIVERY || cartInfoMode === MODES.MIXED) {
      modes.add(cartInfoMode);
      return modes;
    }

    const fallbackMode = getCartFallbackMode(cart);
    for (const item of cart.items) modes.add(getLineFulfillmentMode(item, fallbackMode));
    return modes;
  };

  const getClickCollectLineStores = (cart) => {
    const stores = new Map();
    if (!Array.isArray(cart?.items)) return stores;

    const cartInfo = getCartInfoFromAttributes(cart);
    const cartInfoStore = cartInfo?.store;
    const cartInfoMode = cartInfo?.mode;
    if (cartInfoMode === MODES.CLICK_COLLECT) {
      for (const store of mergeStoreLists(cartInfo?.stores, cartInfoStore)) addStoreToMap(stores, store);

      return stores;
    }

    if (cartInfoMode === MODES.DELIVERY) return stores;

    if (cartInfoMode === MODES.MIXED) {
      for (const store of cartInfo?.stores || []) addStoreToMap(stores, store);
    }

    const fallbackMode = getCartFallbackMode(cart);
    for (const item of cart.items) {
      if (getLineFulfillmentMode(item, fallbackMode) !== MODES.CLICK_COLLECT) continue;

      const store = getStoreFromLineItem(item) || sanitizeStoreForMode(MODES.CLICK_COLLECT, cartInfoStore);
      addStoreToMap(stores, store);
    }

    return stores;
  };

  const getResolvedCartMode = (cart) => {
    const cartInfoMode = getCartInfoFromAttributes(cart)?.mode;
    if (cartInfoMode === MODES.CLICK_COLLECT || cartInfoMode === MODES.DELIVERY || cartInfoMode === MODES.MIXED) {
      return cartInfoMode;
    }

    const fallbackMode = getCartFallbackMode(cart);
    const cartModes = getCartModes(cart);
    if (cartModes.size > 1) return MODES.MIXED;

    if (cartModes.has(MODES.CLICK_COLLECT) && getClickCollectLineStores(cart).size > 1) return MODES.MIXED;

    if (cartModes.size === 1) return [...cartModes][0];

    return fallbackMode;
  };

  const getStoreFromCart = (cart, fulfillmentMode = null) => {
    const mode = normalizeMode(fulfillmentMode, getResolvedCartMode(cart));
    if (mode !== MODES.CLICK_COLLECT) return null;

    const cartInfoStore = getCartInfoFromAttributes(cart)?.store;
    if (cartInfoStore?.id) return sanitizeStoreForMode(mode, cartInfoStore);

    if (getClickCollectLineStores(cart).size > 1) return null;

    if (Array.isArray(cart?.items)) {
      const lineWithStore = cart.items.find((item) => {
        if (getLineFulfillmentMode(item, getCartFallbackMode(cart)) !== MODES.CLICK_COLLECT) return false;

        const lineStoreId = stringify(getStoreFromLineItem(item)?.id).trim();
        return lineStoreId.length > 0;
      });
      if (lineWithStore) {
        return getStoreFromLineItem(lineWithStore);
      }
    }

    return null;
  };

  const getStoreFromFormData = (formData) => {
    if (!formData || typeof formData.get !== 'function') return null;

    const cartInfo = parseCartInfo(formData.get(`properties[${CART_INFO_KEY}]`));
    return cartInfo?.store?.id ? cartInfo.store : null;
  };

  const getModeFromFormData = (formData, fallbackMode = MODES.DELIVERY) => {
    if (!formData || typeof formData.get !== 'function') {
      return normalizeMode(fallbackMode, MODES.DELIVERY);
    }

    const cartInfo = parseCartInfo(formData.get(`properties[${CART_INFO_KEY}]`));
    return cartInfo?.mode || normalizeMode(fallbackMode, MODES.DELIVERY);
  };

  const setFormDataFulfillment = (formData, fulfillmentMode, store) => {
    if (!formData || typeof formData.set !== 'function') return;

    const mode = normalizeLineMode(fulfillmentMode, MODES.DELIVERY);
    const selectedStore = sanitizeStoreForMode(mode, store);
    formData.set(`properties[${CART_INFO_KEY}]`, stringifyLineCartInfo(mode, selectedStore));
  };

  const clearFormDataFulfillment = (formData) => {
    if (!formData || typeof formData.delete !== 'function') return;

    formData.delete(`properties[${CART_INFO_KEY}]`);
    if (!stringify(formData.get('properties[_item_fulfillment_type]')).trim()) {
      formData.delete('properties[_item_fulfillment_type]');
    }
  };

  const buildConflictResult = (hasConflict, reason, { cartMode, requestedMode, cartFallbackMode, cartStore, requestedStore }) => ({
    hasConflict,
    reason,
    cartMode,
    incomingMode: requestedMode,
    cartAttributeMode: cartFallbackMode,
    cartStore: cartStore ?? null,
    incomingStore: requestedStore,
  });

  const evaluateAddConflict = (cart, incomingMode, incomingStore = null) => {
    const cartFallbackMode = getCartFallbackMode(cart);
    const cartMode = getResolvedCartMode(cart);
    const requestedMode = normalizeLineMode(incomingMode, cartFallbackMode);
    const requestedStore = sanitizeStoreForMode(requestedMode, incomingStore);
    const shared = { cartMode, requestedMode, cartFallbackMode, requestedStore };
    const hasItems = Array.isArray(cart?.items) && cart.items.length > 0;

    if (!hasItems) return buildConflictResult(false, 'empty_cart', shared);

    if (cartMode === MODES.MIXED) return buildConflictResult(true, 'mixed_cart', shared);

    const cartStore = getStoreFromCart(cart, cartMode);
    const withStore = { ...shared, cartStore };

    if (cartMode !== requestedMode) return buildConflictResult(true, 'mixed_mode', withStore);

    if (cartMode === MODES.CLICK_COLLECT && cartStore?.id && requestedStore?.id && String(cartStore.id) !== String(requestedStore.id)) {
      return buildConflictResult(true, 'different_store', withStore);
    }

    return buildConflictResult(false, 'no_conflict', withStore);
  };

  const resolveAddContext = ({ cart, requestedMode = null, requestedStore = null } = {}) => {
    const hasItems = Array.isArray(cart?.items) && cart.items.length > 0;
    const fallbackMode = getCartFallbackMode(cart);
    const cartMode = getResolvedCartMode(cart);
    const requestedLineMode = requestedMode == null ? null : normalizeLineMode(requestedMode, fallbackMode);
    const defaultMode = hasItems && cartMode !== MODES.MIXED ? cartMode : fallbackMode;
    const mode = requestedLineMode || defaultMode;
    const selectedStore = sanitizeStoreForMode(mode, requestedStore) || getStoreFromCart(cart, mode) || null;
    if (mode === MODES.CLICK_COLLECT && !selectedStore?.id) {
      return {
        ok: false,
        reason: 'missing_store',
        mode,
        conflict: evaluateAddConflict(cart, mode, selectedStore),
      };
    }

    const conflict = evaluateAddConflict(cart, mode, selectedStore);
    return {
      ok: true,
      mode,
      store: mode === MODES.CLICK_COLLECT ? selectedStore : null,
      conflict,
    };
  };

  const shouldSyncLine = (item, mode, selectedStore, fallbackMode) => {
    const currentMode = getLineFulfillmentMode(item, fallbackMode);
    if (currentMode !== mode) return true;

    const currentStore = getStoreFromLineItem(item);
    const currentStoreId = stringify(currentStore?.id);
    const currentStoreName = getStoreNameFromLineItem(item);
    const nextStoreId = selectedStore?.id || '';
    const nextStoreName = selectedStore?.name || '';
    const expectedCartInfo = stringifyLineCartInfo(mode, selectedStore);
    const currentCartInfo = stringify(item?.properties?.[CART_INFO_KEY]);
    return (currentStoreId && currentStoreId !== nextStoreId)
      || currentStoreName !== nextStoreName
      || currentCartInfo !== expectedCartInfo;
  };

  const fetchCart = async () => {
    const response = await fetch(`${window.theme.routes.cart}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      debug.error('fetchCart:failed', 'non_ok_response', { status: response.status });
      throw new Error('cart_fetch_failed');
    }

    return response.json();
  };

  const sendLineChange = async (change) => {
    let response;
    try {
      response = await fetch(window.theme.routes.cartChange, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(change.payload),
      });
    } catch (error) {
      debug.error('syncCartLineFulfillment:lineFailed', error, {
        line: change.line,
        hasKey: Boolean(change.payload.id),
      });
      return false;
    }

    if (!response.ok) {
      let body = '';
      try {
        body = await response.text();
      } catch {
        /* no-op */
      }

      debug.error('syncCartLineFulfillment:lineFailed', 'non_ok_response', {
        line: change.line,
        hasKey: Boolean(change.payload.id),
        status: response.status,
        body: body.slice(0, 400),
      });
      return false;
    }

    return true;
  };

  const syncCartLineFulfillment = async ({ fulfillmentMode, store, cart: initialCart } = {}) => {
    const cart = initialCart || (await fetchCart());
    if (!Array.isArray(cart?.items) || cart.items.length === 0) return true;

    const mode = normalizeLineMode(fulfillmentMode, getCartFallbackMode(cart));
    const selectedStore = sanitizeStoreForMode(mode, store);
    const fallbackMode = getCartFallbackMode(cart);
    const changes = [];

    for (let lineIndex = cart.items.length - 1; lineIndex >= 0; lineIndex -= 1) {
      const item = cart.items[lineIndex];
      if (!shouldSyncLine(item, mode, selectedStore, fallbackMode)) continue;

      const payload = {
        quantity: item.quantity,
        properties: buildLineProperties(item.properties, mode, selectedStore),
      };
      const lineKey = stringify(item?.key).trim();
      if (lineKey) {
        changes.push({ line: lineIndex + 1, payload: { ...payload, id: lineKey } });
      } else {
        changes.push({ line: lineIndex + 1, payload: { ...payload, line: lineIndex + 1 } });
      }
    }

    if (!changes.length) return true;

    /* Shopify's /cart/change mutates the cart version on every call, so
       concurrent requests always 409. Run all changes sequentially. */
    for (const change of changes) {
      const ok = await sendLineChange(change);
      if (!ok) return false;
    }

    return true;
  };

  return {
    CART_INFO_KEY,
    MODES,
    normalizeMode,
    normalizeStoreList,
    normalizeLineMode,
    buildCartInfoPayload,
    stringifyCartInfo,
    buildLineCartInfoPayload,
    stringifyLineCartInfo,
    parseCartInfo,
    sanitizeStoreForMode,
    buildLineProperties,
    getCartFallbackMode,
    getResolvedCartMode,
    getLineFulfillmentMode,
    getStoreFromLineItem,
    getStoreNameFromLineItem,
    getCartModes,
    getCartInfoStores,
    getStoreFromCart,
    getStoreFromFormData,
    getModeFromFormData,
    setFormDataFulfillment,
    clearFormDataFulfillment,
    evaluateAddConflict,
    resolveAddContext,
    syncCartLineFulfillment,
  };
})();
if (!window.theme) window.theme = {};

window.theme.fulfillmentModeUtil = Object.assign(window.theme.fulfillmentModeUtil || {}, fulfillmentModeUtil);
window.StoreSelector = Object.assign(window.StoreSelector || {}, {
  CART_INFO_KEY,
  MODES,
  EVENTS,
  normalizeMode,
  normalizeStore,
  normalizeStoreList,
  buildCartInfoPayload,
  stringifyCartInfo,
  buildLineCartInfoPayload,
  stringifyLineCartInfo,
  parseCartInfo,
  getCartInfoStores: fulfillmentModeUtil.getCartInfoStores,
  readSelection,
  writeSelection,
  clearSelection,
  noteCartBinding,
  resolveInitialState,
  getStoreCartStock,
  getDeliveryCartStock,
});
